import { useCallback, useState } from "react";
import { observer } from "mobx-react-lite";
import styled from "@emotion/styled";
import { MdsIcon, MdsIconKind } from "@/design-system/components/icon";
import {
  MDSButtonVariant,
  MDSIconButton,
  MDSText,
  MDSTextVariant,
  MDSTooltip,
  MDSTooltipPlacement,
  MDSPanelHeader,
  MDSPanelHeaderSlotName,
  MDSLensGroup,
  MDSTextFieldSlotName,
  MDSIconButtonSize,
  MDSPanelHeaderButtonGroup,
} from "@/mds";
import { AnimationTiming } from "@/design-system/constants/common";
import { LensKind, SortByKind } from "@/modules/lenses/types";
import { FadeHorizontalScroll } from "@/components/horizontal-scroll/FadeHorizontalScroll";
import { CollectionsListPageContactFacetFilter } from "@/pages/collections/components/CollectionsListPageContactFacetFilter";
import { PageBannerSection } from "@/components/banners/page-banner-section";
import { useTabPageStore } from "@/hooks/useTabPageStore";
import { SortDropdownButton } from "@/components/filters/SortDropdownButton";
import { FilterField } from "@/components/filters/FilterField";

export const CollectionsListPageHeader = observer(() => {
  const page = useTabPageStore("collectionsListPage");
  const [showFilters, setShowFilters] = useState<boolean>(false);

  const onCollectionsFilterChange = useCallback(
    (value: string) => {
      page.setCollectionsFilterQuery({ query: value });
    },
    [page]
  );

  const clearCollectionsFilterQuery = useCallback(() => {
    page.setCollectionsFilterQuery({ query: "" });
  }, [page]);

  const tabs = [
    {
      label: "All",
      onClick: () =>
        page.setParams({ lens: LensKind.All, sortBy: SortByKind.LastModified, filters: {} }),
      isActive: page.lens === LensKind.All,
    },
    {
      label: "Created by me",
      onClick: () =>
        page.setParams({
          lens: LensKind.AddedByMe,
          sortBy: SortByKind.LastModified,
          filters: {},
        }),
      isActive: page.lens === LensKind.AddedByMe,
    },
    {
      label: "Shared with me",
      onClick: () =>
        page.setParams({
          lens: LensKind.SharedWithMe,
          sortBy: SortByKind.LastModified,
          filters: {},
        }),
      isActive: page.lens === LensKind.SharedWithMe,
    },
  ];

  return (
    <Wrapper>
      <MDSPanelHeader>
        <MDSText
          slot={MDSPanelHeaderSlotName.Heading}
          variant={MDSTextVariant.HeaderXl}
          numberOfLines={1}
        >
          Collections
        </MDSText>
        <MDSLensGroup slot={MDSPanelHeaderSlotName.Subsection} items={tabs} />
        <MDSPanelHeaderButtonGroup slot={MDSPanelHeaderSlotName.Actions}>
          <MDSTooltip
            config={{
              label: "Create collection",
              placement: MDSTooltipPlacement.Top,
              delaySeconds: AnimationTiming.TooltipHoverDelaySeconds,
            }}
          >
            <MDSIconButton
              iconKind={MdsIconKind.Plus}
              onClick={page.handleCreateNewCollection}
              size={MDSIconButtonSize.Medium}
            />
          </MDSTooltip>
          <MDSTooltip
            config={{
              label: "Show filters",
              placement: MDSTooltipPlacement.Top,
              delaySeconds: AnimationTiming.TooltipHoverDelaySeconds,
            }}
          >
            <MDSIconButton
              iconKind={MdsIconKind.Filter}
              onClick={() => setShowFilters(!showFilters)}
              isToggled={showFilters}
              size={MDSIconButtonSize.Medium}
            />
          </MDSTooltip>
        </MDSPanelHeaderButtonGroup>
      </MDSPanelHeader>
      {showFilters && (
        <WrapperBottom>
          <FiltersContainer>
            <FilterField
              placeholder="Filter by name"
              value={page.collectionsFilterQuery}
              onChange={onCollectionsFilterChange}
              onClearText={clearCollectionsFilterQuery}
              showClearTextButton={true}
              autoFocus={true}
            >
              <MdsIcon kind={MdsIconKind.Search} slot={MDSTextFieldSlotName.LeftAddon} />
            </FilterField>
            <SortDropdownButton
              isToggled={page.sortBy !== SortByKind.LastModified}
              contentList={page.sortOptions}
              label={page.sortLabel}
              placement="below-right-alignment"
              dropdownIcon
              variant={MDSButtonVariant.Default}
            />
            <CollectionsListPageContactFacetFilter
              buttonPrefix="Edited by"
              selectedSpaceAccountIds={page.params.filters.modifiedBy}
              onSelectItem={page.toggleContactInModifiedBy}
              onSelectClear={page.clearModifiedBy}
            />
            <CollectionsListPageContactFacetFilter
              buttonPrefix="Created by"
              selectedSpaceAccountIds={page.params.filters.createdBy}
              onSelectItem={page.toggleContactInCreatedBy}
              onSelectClear={page.clearCreatedBy}
            />
          </FiltersContainer>
        </WrapperBottom>
      )}
      <PageBannerSection />
    </Wrapper>
  );
});

const Wrapper = styled.div({});

const WrapperBottom = styled.div(({ theme }) => ({
  padding: theme.spacing.md,
}));

const FiltersContainer = styled(FadeHorizontalScroll)(({ theme }) => ({
  display: "flex",
  flexDirection: "row",
  alignItems: "center",
  justifyContent: "flex-start",
  gap: theme.spacing.sm,
}));
